import { useAccount } from '@nizza/core';
import { useMutation } from '@tanstack/react-query';
import { SetStateAction } from 'jotai';
import { Dispatch } from 'react';
import { highlightProductProvider } from '../../../core';
import { MAX_HIGHLIGHT_PRODUCT, Product } from '../../../shared';

type Props = {
  collectionId: string;
  eventId: string;
  selectedProducts: string[];
  setSelectedProducts: Dispatch<SetStateAction<string[]>>;
};

export type onSwitchProps = {
  value: boolean;
  product: Product;
};

type HighlightProductMutationArgs = {
  productIds: string[] | [];
};

export const useHighlightProductAction = ({
  collectionId,
  eventId,
  selectedProducts,
  setSelectedProducts,
}: Props) => {
  const { accountName } = useAccount();

  const highlightProductMutation = useMutation({
    mutationKey: ['highlightProduct', accountName, collectionId, eventId],
    mutationFn: async ({ productIds }: HighlightProductMutationArgs) => {
      if (!accountName) return;

      highlightProductProvider.highlightProduct({
        account: accountName,
        idEvent: eventId,
        productIds,
        showProduct: !!productIds.length,
        collectionId,
      });
    },
  });

  const highlightProduct = async (sku: string) => {
    const newSelectedProducts = [sku, ...selectedProducts];

    if (newSelectedProducts.length > MAX_HIGHLIGHT_PRODUCT) {
      newSelectedProducts.pop();
    }

    setSelectedProducts(newSelectedProducts);

    highlightProductMutation.mutate({ productIds: newSelectedProducts });
  };

  const unHighlightProduct = async (sku: string) => {
    const newSelectedProducts = selectedProducts.filter(item => item !== sku);

    setSelectedProducts(newSelectedProducts);
    highlightProductMutation.mutate({ productIds: newSelectedProducts });
  };

  const onSwitch = ({ value, product }: onSwitchProps) => {
    if (value) {
      highlightProduct(product.id);
    } else {
      unHighlightProduct(product.id);
    }
  };

  return {
    onSwitch,
  };
};
